import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { Trash2, Send, Check } from 'lucide-react';
import { submitPortalFulfillmentRequest } from '../../services/portalFulfillmentRequestService';
import { getTermEndDateFromAccount } from '../../utils/accountProgramDeliverables';
import {
  computeTotalPriceFromSelection,
  isStrategyCallItem,
  isAlwaysRequiredItem,
  isHiringServiceItem,
  clampDeliverableQuantity,
  getDeliverableMaxQuantity,
  computeCustomProgramMonthlyBaseFromAccount,
  popularAddOnLineAmount,
  isAlaCarteItemHiddenFromPurchase,
} from '../../utils/programPricing';
import { areAlaCarteItemsExclusiveChoice } from '../../utils/alaCarteExclusiveChoice';
import { sortCatalogByDeliverableOrder } from '../../utils/deliverableOrder';

function catalogKey(item) {
  return item.deliverableCatalogId || item.id;
}

const isOnSiteVisitName = (name) => /on[- ]?site\s*visit|onsite\s*visit/i.test(name || '');

/** Catalog / package pricing unit — not `deliverable.period`. */
function getCatalogUnitRaw(item) {
  const sp = item?.standardPricing && typeof item.standardPricing === 'object' ? item.standardPricing : {};
  const u = sp.unit != null && String(sp.unit).trim() !== '' ? String(sp.unit).trim() : '';
  if (u) return u;
  const pkg0 = item?.packages?.[0];
  const line0 = pkg0?.items?.[0];
  if (line0 && line0.unit != null && String(line0.unit).trim() !== '') return String(line0.unit).trim();
  return '';
}

function humanizeCatalogUnit(raw) {
  const key = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const map = {
    month: 'Per month',
    monthly: 'Per month',
    mo: 'Per month',
    year: 'Per year',
    annually: 'Per year',
    annual: 'Per year',
    quarter: 'Per quarter',
    quarterly: 'Per quarter',
    hour: 'Per hour',
    item: 'Per item',
    session: 'Per session',
    seat: 'Per seat',
    user: 'Per user',
    'one time': 'One-time',
    onetime: 'One-time',
    'one-time': 'One-time',
    lump: 'One-time',
    package: 'Per package',
    term: 'Per term',
  };
  if (map[key]) return map[key];
  if (!key) return '';
  return `Per ${raw.trim()}`;
}

/** When the catalog omits `standardPricing.unit`, infer a billing label (never deliverable.period). */
function catalogUnitFallbackLabel(item, billingCycleRaw) {
  if (item.pricingType === 'term' && !isHiringServiceItem(item)) return 'Per term';
  if (isStrategyCallItem(item) || isHiringServiceItem(item)) return 'Per month';
  const bc = String(billingCycleRaw || 'monthly').toLowerCase();
  if (bc === 'annual' || bc === 'year') return 'Per year (billed annually)';
  if (bc === 'quarterly' || bc === 'quarter') return 'Per quarter (billed quarterly)';
  return 'Per month';
}

function catalogUnitDisplayLabel(item, billingCycleRaw) {
  const raw = getCatalogUnitRaw(item);
  const fromCat = humanizeCatalogUnit(raw);
  if (fromCat) return fromCat;
  return catalogUnitFallbackLabel(item, billingCycleRaw);
}

/** List-style line charge (no term spread) + catalog pricing unit for display. */
function getPopularCardPricing(item, billingCycleRaw, qtyOverride = 1) {
  if (item.pricingType === 'caseByCase') return { quote: true };
  const qty = clampDeliverableQuantity(item, qtyOverride);
  const q = { [item.id]: qty };
  const raw = popularAddOnLineAmount(item, item.id, q);
  if (raw == null) return { quote: true };
  const amount = Math.round(raw);
  const unitLabel = catalogUnitDisplayLabel(item, billingCycleRaw);
  return { quote: false, amount, unitLabel };
}

/** Single string for CRM payloads / notifications. */
function formatPopularPriceLine(item, billingCycleRaw, qtyOverride = 1) {
  const p = getPopularCardPricing(item, billingCycleRaw, qtyOverride);
  if (p.quote) return 'Quote required';
  return `~$${p.amount.toLocaleString()} · ${p.unitLabel}`;
}

/**
 * After setting primaryId to a positive qty, drop exclusive peers to their contract floor (or remove).
 * Returns null if peers cannot be cleared (e.g. locked at enrolled minimum).
 */
function resolveExclusiveChoicePeers(next, primaryId, primaryItem, primaryQty, catalogItems, enrolledQuantities, canReducePlan) {
  if (!primaryItem || primaryQty <= 0) return next;
  const out = { ...next };
  out[primaryId] = primaryQty;
  for (const key of Object.keys(out)) {
    if (key === primaryId) continue;
    const peerQty = out[key] ?? 0;
    if (peerQty <= 0) continue;
    const peerItem = catalogItems.find((i) => i.id === key);
    if (!peerItem || !areAlaCarteItemsExclusiveChoice(primaryItem, peerItem)) continue;
    const enr = enrolledQuantities[key] ?? 0;
    const floor = canReducePlan ? 0 : enr;
    if (peerQty > floor) {
      if (floor <= 0) delete out[key];
      else out[key] = floor;
    } else {
      return null;
    }
  }
  return out;
}

export default function PortalBillingMyPlan({
  darkMode,
  currentUser,
  accountId,
  account,
  catalogItems,
  programItems,
  planLoading,
  onReload,
}) {
  const [draftQuantities, setDraftQuantities] = useState({});
  const [enrolledQuantities, setEnrolledQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showCustomizeProgram, setShowCustomizeProgram] = useState(false);
  const [popularRequestQuantities, setPopularRequestQuantities] = useState({});
  const [popularDetailItem, setPopularDetailItem] = useState(null);
  const [popularRequestSubmitting, setPopularRequestSubmitting] = useState(false);
  const [popularRequestSent, setPopularRequestSent] = useState(false);

  useEffect(() => {
    setPopularRequestSent(false);
    setPopularRequestQuantities({});
  }, [accountId]);

  useEffect(() => {
    const q = {};
    (programItems || []).forEach((i) => {
      const id = catalogKey(i);
      q[id] = Number(i.programQuantity) || 1;
    });
    const catList = catalogItems || [];
    Object.keys(q).forEach((id) => {
      const cat = catList.find((c) => c.id === id);
      if (cat && q[id] > 0) q[id] = clampDeliverableQuantity(cat, q[id]);
    });
    setEnrolledQuantities(q);
    setDraftQuantities({ ...q });
  }, [accountId, programItems, catalogItems]);

  const billingCycleRaw =
    account?.program?.billingCycle || account?.billingCycle || 'monthly';

  const termEnd = useMemo(() => getTermEndDateFromAccount(account, null), [account]);

  /** Matches ClientOverview: no plan reductions until on or after Term End (inclusive by calendar day). Unknown term ⇒ add-only. */
  const canReducePlan = useMemo(() => {
    if (!termEnd) return false;
    const end = new Date(termEnd);
    if (Number.isNaN(end.getTime())) return false;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() >= end.getTime();
  }, [termEnd]);

  const enrolledIds = useMemo(() => new Set(Object.keys(enrolledQuantities)), [enrolledQuantities]);
  const draftIds = useMemo(() => new Set(Object.keys(draftQuantities)), [draftQuantities]);

  const enrolledSum = useMemo(
    () => computeTotalPriceFromSelection(enrolledIds, enrolledQuantities, catalogItems || [], billingCycleRaw),
    [enrolledIds, enrolledQuantities, catalogItems, billingCycleRaw]
  );

  const draftSum = useMemo(
    () => computeTotalPriceFromSelection(draftIds, draftQuantities, catalogItems || [], billingCycleRaw),
    [draftIds, draftQuantities, catalogItems, billingCycleRaw]
  );

  const storedMonthly = useMemo(
    () => Number(account?.program?.monthlyAmount) || Number(account?.billing?.monthlyAmount) || 0,
    [account]
  );

  const customProgramMonthlyBase = useMemo(
    () => computeCustomProgramMonthlyBaseFromAccount(account, catalogItems || [], billingCycleRaw),
    [account, catalogItems, billingCycleRaw]
  );

  /** ClientOverview order: program/billing monthly when set; else custom catalog base; else enrolled lines. */
  const currentInvestmentRounded = useMemo(() => {
    if (storedMonthly > 0) return Math.round(storedMonthly);
    if (customProgramMonthlyBase != null && customProgramMonthlyBase > 0) return Math.round(customProgramMonthlyBase);
    return Math.round(enrolledSum);
  }, [storedMonthly, customProgramMonthlyBase, enrolledSum]);

  const currentMonthlyLabel = useMemo(() => {
    if (storedMonthly > 0) return 'Billed on your account';
    if (customProgramMonthlyBase != null && customProgramMonthlyBase > 0) return 'Custom program (catalog)';
    return 'From enrolled lines (estimate)';
  }, [storedMonthly, customProgramMonthlyBase]);

  const monthlyAdjustmentRounded = Math.round(draftSum - enrolledSum);
  const projectedTotalRounded = Math.round(draftSum);

  const deltaVsEnrolled = Math.round(draftSum - enrolledSum);

  const hasChanges = useMemo(() => {
    const keys = new Set([...Object.keys(draftQuantities), ...Object.keys(enrolledQuantities)]);
    for (const k of keys) {
      if ((draftQuantities[k] ?? 0) !== (enrolledQuantities[k] ?? 0)) return true;
    }
    return false;
  }, [draftQuantities, enrolledQuantities]);

  const addableCatalog = useMemo(() => {
    const filtered = (catalogItems || []).filter((item) => {
      if (item.pricingType === 'child') return false;
      if (item.pricingType === 'alwaysIncluded') {
        const onContract = (programItems || []).some((p) => catalogKey(p) === item.id);
        const isPortalAccessName = /portal\s*access/i.test(item.name || '');
        if (!onContract && !isPortalAccessName) return false;
      }
      if (isOnSiteVisitName(item.name)) return false;
      // Unpromoted items aren't for sale, but stay visible for clients already on them.
      if (
        isAlaCarteItemHiddenFromPurchase(item) &&
        !enrolledIds.has(item.id) &&
        !draftIds.has(item.id)
      ) {
        return false;
      }
      return true;
    });
    return sortCatalogByDeliverableOrder(filtered);
  }, [catalogItems, programItems, enrolledIds, draftIds]);

  const popularAddOnItems = useMemo(
    () =>
      sortCatalogByDeliverableOrder(
        (catalogItems || []).filter(
          (item) =>
            item.popularAddOn === true &&
            item.pricingType !== 'child' &&
            !isAlaCarteItemHiddenFromPurchase(item)
        )
      ),
    [catalogItems]
  );

  const popularSelectionIds = useMemo(
    () => Object.keys(popularRequestQuantities).filter((id) => (popularRequestQuantities[id] ?? 0) > 0),
    [popularRequestQuantities]
  );

  const hasPopularSelection = popularSelectionIds.length > 0;

  /** Sum of catalog list lines for selected popular add-ons (no term amortization). */
  const selectedPopularListTotal = useMemo(() => {
    if (!hasPopularSelection) return 0;
    let sum = 0;
    for (const id of popularSelectionIds) {
      const it = (catalogItems || []).find((c) => c.id === id);
      if (!it) continue;
      const q = { [id]: clampDeliverableQuantity(it, popularRequestQuantities[id] ?? 1) };
      const line = popularAddOnLineAmount(it, id, q);
      if (typeof line === 'number') sum += line;
    }
    return Math.round(sum);
  }, [hasPopularSelection, popularSelectionIds, popularRequestQuantities, catalogItems]);

  const togglePopularCard = useCallback(
    (item) => {
      if (popularRequestSent) return;
      const id = item.id;
      setPopularRequestQuantities((prev) => {
        const cur = prev[id] ?? 0;
        const next = { ...prev };
        if (cur > 0) {
          delete next[id];
          return next;
        }
        let initial = 1;
        const period = (item.deliverable?.period || '').toLowerCase();
        if (item.hasDeliverable && ['month', 'one time', 'onetime'].includes(period)) {
          const defaultQty = parseInt(item.deliverable?.quantity, 10) || 1;
          initial = clampDeliverableQuantity(item, defaultQty);
        } else if (isStrategyCallItem(item) || isHiringServiceItem(item)) {
          initial = clampDeliverableQuantity(item, 1);
        }
        next[id] = initial;
        const resolved = resolveExclusiveChoicePeers(
          next,
          id,
          item,
          initial,
          catalogItems || [],
          {},
          true
        );
        if (!resolved) return prev;
        return resolved;
      });
    },
    [catalogItems, popularRequestSent]
  );

  const adjustPopularQty = useCallback(
    (itemId, delta) => {
      if (popularRequestSent) return;
      setPopularRequestQuantities((prev) => {
        const item = (catalogItems || []).find((c) => c.id === itemId);
        if (!item) return prev;
        const cur = prev[itemId] ?? 0;
        if (cur <= 0) return prev;
        const next = { ...prev };
        const stepped = cur + delta;
        if (stepped < 1) {
          delete next[itemId];
          return next;
        }
        next[itemId] = clampDeliverableQuantity(item, stepped);
        return next;
      });
    },
    [catalogItems, popularRequestSent]
  );

  const handlePopularAdditionalItemsRequest = useCallback(async () => {
    if (!currentUser?.uid || !accountId || !hasPopularSelection || popularRequestSent) return;
    setPopularRequestSubmitting(true);
    try {
      let requestedBy = currentUser.displayName || currentUser.email || '';
      let company = '';
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../firebase');
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const ud = userDoc.data();
          const full = `${ud?.firstName || ''} ${ud?.lastName || ''}`.trim();
          if (full) requestedBy = full;
          const c = ud?.company;
          if (c && c !== 'Unknown Company') company = c;
        }
      } catch (_) {}

      const items = popularSelectionIds
        .map((id) => {
          const it = (catalogItems || []).find((c) => c.id === id);
          if (!it) return null;
          const q = clampDeliverableQuantity(it, popularRequestQuantities[id] ?? 1);
          return {
            id: it.id,
            name: it.name,
            quantity: q,
            description: it.description || '',
            priceLine: formatPopularPriceLine(it, billingCycleRaw, q),
          };
        })
        .filter(Boolean);

      await submitPortalFulfillmentRequest({
        kind: 'program',
        source: 'billing',
        details: {
          kind: 'additional_items_request',
          subkind: 'popular_add_ons',
          programName: account?.program?.status || account?.program?.name || 'Program',
          billingCycle: billingCycleRaw,
          itemIds: popularSelectionIds,
          items,
          estimatedMonthlyTotal: selectedPopularListTotal,
        },
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        accountId,
        requestedBy,
        company,
      });
      window.alert('Your request has been sent. Our team will follow up soon!');
      setPopularRequestSent(true);
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Could not send request.');
    } finally {
      setPopularRequestSubmitting(false);
    }
  }, [
    currentUser,
    accountId,
    hasPopularSelection,
    popularSelectionIds,
    popularRequestQuantities,
    catalogItems,
    billingCycleRaw,
    account,
    selectedPopularListTotal,
    popularRequestSent,
  ]);

  const adjustDraft = useCallback(
    (itemId, delta, mode) => {
      setDraftQuantities((prev) => {
        const item = catalogItems.find((i) => i.id === itemId);
        const isStrat = item && isStrategyCallItem(item);
        let minQty = 0;
        if (enrolledQuantities[itemId] != null) minQty = enrolledQuantities[itemId];

        if (mode === 'remove') {
          const cur = prev[itemId] ?? 0;
          if (!canReducePlan && cur <= minQty) return prev;
          let floor = canReducePlan ? 0 : minQty;
          if (item?.pricingType === 'alwaysIncluded' || isAlwaysRequiredItem(item)) floor = Math.max(floor, 1);
          const n = Math.max(floor, cur + delta);
          const next = { ...prev };
          if (n <= 0) delete next[itemId];
          else next[itemId] = item ? clampDeliverableQuantity(item, n) : n;
          return next;
        }

        const cur = prev[itemId] ?? 0;
        let n = Math.max(minQty, cur + delta);
        if (!isStrat && n < 1) return prev;
        if (isStrat && n < 1) return prev;
        if (item && n > 0) {
          const capped = clampDeliverableQuantity(item, n);
          if (delta > 0 && capped === cur) return prev;
          n = capped;
        }

        const next = { ...prev };
        if (n <= 0) delete next[itemId];
        else next[itemId] = n;

        if (n > 0 && item) {
          const resolved = resolveExclusiveChoicePeers(
            next,
            itemId,
            item,
            n,
            catalogItems || [],
            enrolledQuantities,
            canReducePlan
          );
          if (!resolved) return prev;
          return resolved;
        }

        return next;
      });
    },
    [catalogItems, enrolledQuantities, canReducePlan]
  );

  const handleSubmit = async () => {
    if (!currentUser?.uid || !accountId || !hasChanges) return;
    setSubmitting(true);
    try {
      let requestedBy = currentUser.displayName || currentUser.email || '';
      let company = '';
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../firebase');
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const ud = userDoc.data();
          const full = `${ud?.firstName || ''} ${ud?.lastName || ''}`.trim();
          if (full) requestedBy = full;
          const c = ud?.company;
          if (c && c !== 'Unknown Company') company = c;
        }
      } catch (_) {}

      await submitPortalFulfillmentRequest({
        kind: 'program',
        source: 'billing',
        details: {
          kind: 'plan_change',
          programName: account?.program?.status || account?.program?.name || 'Program',
          billingCycle: billingCycleRaw,
          accountMonthlyAmount: storedMonthly || null,
          enrolledQuantities,
          draftQuantities,
          enrolledSelectionMonthly: enrolledSum,
          draftSelectionMonthly: draftSum,
          deltaMonthly: deltaVsEnrolled,
          termEndDate: termEnd ? termEnd.toISOString() : null,
          pastMinimumTerm: canReducePlan,
          catalogItemIdsInDraft: Object.keys(draftQuantities),
        },
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        accountId,
        requestedBy,
        company,
      });
      alert('Your plan change request has been submitted. Our team will confirm billing updates.');
      if (onReload) onReload();
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (planLoading) {
    return (
      <div className={`rounded-xl border p-8 text-center ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Loading your plan…</p>
      </div>
    );
  }

  const programStatus =
    typeof account?.program === 'string' ? account.program : account?.program?.status;
  if (!programStatus || programStatus === 'Not Started') {
    return (
      <div className={`rounded-xl border p-8 text-center ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>No active program linked to your account yet.</p>
      </div>
    );
  }

  return (
    <div id="portal-plan" className="space-y-8">
      {/* Popular Add Ons — from catalog popularAddOn flag */}
      <div>
        <h3 className={`text-lg font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Popular Add Ons
        </h3>
        {popularAddOnItems.length === 0 ? (
          <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No featured add-ons are marked in the catalog yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {popularAddOnItems.map((item) => {
              const id = item.id;
              const qty = popularRequestQuantities[id] ?? 0;
              const isSelected = qty > 0;
              const deliverablePeriod = (item.deliverable?.period || '').toLowerCase();
              const hasMonthDeliverable = item.hasDeliverable && deliverablePeriod === 'month';
              const hasOneTimeDeliverable =
                item.hasDeliverable && (deliverablePeriod === 'one time' || deliverablePeriod === 'onetime');
              const hasQuantityControls =
                hasMonthDeliverable ||
                hasOneTimeDeliverable ||
                isStrategyCallItem(item) ||
                isHiringServiceItem(item);
              const maxQ = getDeliverableMaxQuantity(item);
              const isSingleUnitCard = maxQ === 1;
              const atMax = maxQ != null && qty >= maxQ;
              const popPrice = getPopularCardPricing(item, billingCycleRaw, isSelected ? qty : 1);
              const cardLocked = popularRequestSent;

              return (
                <div
                  key={id}
                  role={cardLocked ? 'presentation' : 'button'}
                  tabIndex={cardLocked ? -1 : 0}
                  onClick={() => !cardLocked && togglePopularCard(item)}
                  onKeyDown={
                    cardLocked
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            togglePopularCard(item);
                          }
                        }
                  }
                  className={`relative rounded-xl border-2 p-3 flex flex-col items-center justify-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 focus-visible:ring-offset-2 ${
                    darkMode ? 'focus-visible:ring-offset-gray-900' : 'focus-visible:ring-offset-white'
                  } ${cardLocked ? 'cursor-default opacity-80' : 'cursor-pointer active:scale-[0.98]'} ${
                    isSelected
                      ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/30'
                      : darkMode
                        ? 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPopularDetailItem(item);
                    }}
                    className={`absolute top-2 left-2 p-1 rounded-full transition-colors ${
                      darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                    }`}
                    aria-label="View item details"
                  >
                    <FaInfoCircle
                      className={`h-4 w-4 transition-colors ${
                        darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'
                      }`}
                    />
                  </button>
                  {isSelected && (
                    <Check
                      className="text-green-600 dark:text-green-400 w-5 h-5 absolute top-2 right-2"
                      strokeWidth={3}
                      aria-hidden
                    />
                  )}
                  <span className="text-2xl mb-1" aria-hidden>
                    {item.icon || '📦'}
                  </span>
                  <h4
                    className={`font-semibold text-xs text-center px-6 ${
                      isSelected ? 'text-green-800 dark:text-green-200' : darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {item.name}
                  </h4>
                  {popPrice.quote ? (
                    <p
                      className={`text-[10px] mt-1 font-medium ${
                        darkMode ? 'text-amber-300/90' : 'text-amber-800'
                      }`}
                    >
                      Quote required
                    </p>
                  ) : (
                    <div className="mt-1 text-center w-full px-1">
                      <p
                        className={`text-sm font-semibold tabular-nums leading-tight ${
                          darkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}
                      >
                        ~${popPrice.amount.toLocaleString()}
                      </p>
                      <p
                        className={`text-[10px] mt-0.5 font-medium leading-snug ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {popPrice.unitLabel}
                      </p>
                    </div>
                  )}
                  {hasQuantityControls && isSelected && (
                    <div
                      className="flex items-center gap-2 mt-2"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <button
                        type="button"
                        disabled={cardLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustPopularQty(id, -1);
                        }}
                        className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-50"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="min-w-[1.5rem] text-center font-semibold text-gray-900 dark:text-white text-sm tabular-nums">
                        {qty}
                      </span>
                      <button
                        type="button"
                        disabled={cardLocked || atMax}
                        onClick={(e) => {
                          e.stopPropagation();
                          adjustPopularQty(id, 1);
                        }}
                        className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-50"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  )}
                  {!isSelected && (
                    <p className={`text-[10px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Click to add</p>
                  )}
                  {isSelected && !hasQuantityControls && isSingleUnitCard && (
                    <p className={`text-[10px] mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Click card to remove</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-3xl mx-auto mt-5">
          <button
            type="button"
            disabled={!hasPopularSelection || popularRequestSubmitting || popularRequestSent}
            onClick={handlePopularAdditionalItemsRequest}
            title={
              popularRequestSent
                ? 'Request already sent'
                : !hasPopularSelection
                  ? 'Select one or more add-ons above'
                  : undefined
            }
            className={`w-full sm:w-1/2 py-3 px-4 rounded-lg font-semibold shadow-md transition-colors inline-flex items-center justify-center gap-2 ${
              popularRequestSent
                ? 'bg-green-600 text-white cursor-default'
                : !hasPopularSelection || popularRequestSubmitting
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {popularRequestSent ? (
              <>
                <Check className="w-5 h-5" strokeWidth={3} />
                Request sent
              </>
            ) : popularRequestSubmitting ? (
              'Sending…'
            ) : (
              'Request Additional Items To My Program'
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowCustomizeProgram((v) => !v)}
            className={`w-full sm:w-1/2 py-3 px-4 rounded-lg font-semibold shadow-md transition-colors ${
              showCustomizeProgram
                ? 'bg-gray-600 hover:bg-gray-700 text-white'
                : darkMode
                  ? 'border-2 border-gray-500 text-white hover:bg-gray-800'
                  : 'border-2 border-gray-300 text-gray-900 hover:bg-gray-50'
            }`}
          >
            {showCustomizeProgram ? 'Close customization' : 'Customize My Program'}
          </button>
        </div>
      </div>

      {showCustomizeProgram && (
        <>
      {/* My Program — ProgramDetail-style grid */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>My Program</h3>
        {addableCatalog.length === 0 ? (
          <div className={`rounded-lg border p-6 text-center ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
            No catalog items available.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {addableCatalog.map((item) => {
              const id = item.id;
              const qty = draftQuantities[id] ?? 0;
              const enrolled = enrolledQuantities[id] ?? 0;
              const isSelected = qty > 0;
              const minusDisabled = !canReducePlan && qty <= enrolled;
              const deliverablePeriod = (item.deliverable?.period || '').toLowerCase();
              const hasMonthDeliverable = item.hasDeliverable && deliverablePeriod === 'month';
              const hasOneTimeDeliverable =
                item.hasDeliverable && (deliverablePeriod === 'one time' || deliverablePeriod === 'onetime');
              const hasQuantityControls =
                hasMonthDeliverable || hasOneTimeDeliverable || isStrategyCallItem(item) || isHiringServiceItem(item);
              const maxQ = getDeliverableMaxQuantity(item);
              const isSingleUnitCard = maxQ === 1;
              const plusDisabled = !isSingleUnitCard && maxQ != null && qty >= maxQ;
              const removeBlockedSingle = isSingleUnitCard && qty > 0 && !canReducePlan && qty <= enrolled;
              const cardClickable = isSingleUnitCard;
              const cardCursor = cardClickable
                ? removeBlockedSingle
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer active:scale-[0.98]'
                : '';

              return (
                <div
                  key={id}
                  role={cardClickable ? 'button' : undefined}
                  tabIndex={cardClickable ? 0 : undefined}
                  aria-pressed={cardClickable ? (qty > 0) : undefined}
                  onKeyDown={
                    cardClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!removeBlockedSingle) {
                              if (qty <= 0) adjustDraft(id, 1, 'add');
                              else adjustDraft(id, -1, 'remove');
                            }
                          }
                        }
                      : undefined
                  }
                  onClick={
                    cardClickable
                      ? () => {
                          if (!removeBlockedSingle) {
                            if (qty <= 0) adjustDraft(id, 1, 'add');
                            else adjustDraft(id, -1, 'remove');
                          }
                        }
                      : undefined
                  }
                  className={`relative rounded-xl border-2 p-3 flex flex-col items-center justify-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 focus-visible:ring-offset-2 ${darkMode ? 'focus-visible:ring-offset-gray-900' : 'focus-visible:ring-offset-white'} ${cardCursor} ${
                    isSelected
                      ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/30'
                      : darkMode
                        ? 'border-gray-600 bg-gray-700/30 hover:border-gray-500'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  {isSelected && (
                    <Check className="text-green-600 dark:text-green-400 w-5 h-5 absolute top-2 right-2" strokeWidth={3} />
                  )}
                  <span className="text-2xl mb-1">{item.icon || '📦'}</span>
                  <h4
                    className={`font-semibold text-xs text-center px-1 ${
                      isSelected ? 'text-green-800 dark:text-green-200' : darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {item.name}
                  </h4>
                  {item.pricingType === 'caseByCase' && (
                    <span
                      className="text-[10px] text-amber-600 dark:text-amber-400 mt-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Quote required
                    </span>
                  )}

                  {/* Quantity: single-unit (max 1) = click card; else +/- */}
                  {isSingleUnitCard ? (
                    <div className="mt-3 w-full px-1">
                      <p
                        className={`text-[10px] text-center leading-snug ${
                          removeBlockedSingle
                            ? darkMode
                              ? 'text-amber-300/90'
                              : 'text-amber-800'
                            : darkMode
                              ? 'text-gray-400'
                              : 'text-gray-600'
                        }`}
                      >
                        {qty <= 0
                          ? 'Click card to add'
                          : removeBlockedSingle
                            ? 'Cannot remove until term end'
                            : 'Click card to remove'}
                      </p>
                      {qty > 0 && (canReducePlan || qty > enrolled) && (
                        <div className="flex justify-center mt-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDraftQuantities((prev) => {
                                const next = { ...prev };
                                let floor = enrolledQuantities[id] ?? 0;
                                const cat = (catalogItems || []).find((c) => c.id === id);
                                if (cat && floor > 0) floor = clampDeliverableQuantity(cat, floor);
                                if (floor <= 0) delete next[id];
                                else next[id] = floor;
                                return next;
                              });
                            }}
                            className="p-1.5 rounded-full text-red-500 hover:bg-red-500/15"
                            title="Reset to enrolled"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        className="flex items-center gap-2 mt-2"
                        onClick={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <button
                          type="button"
                          disabled={minusDisabled}
                          onClick={() => adjustDraft(id, -1, 'remove')}
                          className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                          title={minusDisabled ? 'Cannot reduce until term end' : 'Decrease'}
                        >
                          −
                        </button>
                        <span className="min-w-[1.5rem] text-center font-semibold text-gray-900 dark:text-white text-sm tabular-nums">
                          {qty}
                        </span>
                        <button
                          type="button"
                          disabled={plusDisabled}
                          onClick={() => adjustDraft(id, 1, 'add')}
                          className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                          title={plusDisabled && maxQ != null ? `Maximum quantity is ${maxQ}` : 'Increase'}
                        >
                          +
                        </button>
                        {qty > 0 && (canReducePlan || qty > enrolled) && (
                          <button
                            type="button"
                            onClick={() => {
                              setDraftQuantities((prev) => {
                                const next = { ...prev };
                                let floor = enrolledQuantities[id] ?? 0;
                                const cat = (catalogItems || []).find((c) => c.id === id);
                                if (cat && floor > 0) floor = clampDeliverableQuantity(cat, floor);
                                if (floor <= 0) delete next[id];
                                else next[id] = floor;
                                return next;
                              });
                            }}
                            className="ml-1 p-1 rounded-full text-red-500 hover:bg-red-500/15"
                            title="Reset to enrolled"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {!isSelected && !hasQuantityControls && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 text-center">Use + to add</p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Investment impact — current + adjustment = projected (same catalog math) */}
      <div className={`rounded-xl border p-6 ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
        <h4 className={`text-sm font-semibold uppercase tracking-wide mb-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Monthly Investment impact
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div
            className={`rounded-xl border-2 p-5 text-center shadow-sm ${
              darkMode
                ? 'border-blue-600/80 bg-gradient-to-br from-blue-900/50 to-blue-950/40'
                : 'border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/80'
            }`}
          >
            <div className={`text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Current monthly
            </div>
            <div className={`text-3xl font-bold tabular-nums ${darkMode ? 'text-white' : 'text-blue-900'}`}>
              ${currentInvestmentRounded.toLocaleString()}
            </div>
            <div className={`text-xs mt-2 ${darkMode ? 'text-blue-200/90' : 'text-blue-800/80'}`}>
              {currentMonthlyLabel}
              {currentInvestmentRounded !== Math.round(enrolledSum) ? (
                <span className={`block mt-1 ${darkMode ? 'text-blue-300/80' : 'text-blue-900/70'}`}>
                  Catalog lines: ${Math.round(enrolledSum).toLocaleString()}
                </span>
              ) : null}
            </div>
          </div>
          <div
            className={`rounded-xl border-2 p-5 text-center shadow-sm ${
              monthlyAdjustmentRounded === 0
                ? darkMode
                  ? 'border-gray-600 bg-gray-800/60'
                  : 'border-gray-200 bg-white'
                : monthlyAdjustmentRounded > 0
                  ? darkMode
                    ? 'border-amber-600/70 bg-gradient-to-br from-amber-900/40 to-orange-950/30'
                    : 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50'
                  : darkMode
                    ? 'border-teal-600/70 bg-gradient-to-br from-teal-900/40 to-emerald-950/30'
                    : 'border-teal-300 bg-gradient-to-br from-teal-50 to-emerald-50'
            }`}
          >
            <div
              className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                monthlyAdjustmentRounded === 0
                  ? darkMode
                    ? 'text-gray-400'
                    : 'text-gray-500'
                  : monthlyAdjustmentRounded > 0
                    ? darkMode
                      ? 'text-amber-200'
                      : 'text-amber-900'
                    : darkMode
                      ? 'text-teal-200'
                      : 'text-teal-900'
              }`}
            >
              Monthly adjustment
            </div>
            <div
              className={`text-3xl font-bold tabular-nums ${
                monthlyAdjustmentRounded === 0
                  ? darkMode
                    ? 'text-gray-300'
                    : 'text-gray-700'
                  : monthlyAdjustmentRounded > 0
                    ? darkMode
                      ? 'text-amber-100'
                      : 'text-amber-950'
                    : darkMode
                      ? 'text-teal-100'
                      : 'text-teal-900'
              }`}
            >
              {monthlyAdjustmentRounded > 0 ? '+' : ''}
              ${monthlyAdjustmentRounded.toLocaleString()}
            </div>
            <div
              className={`text-xs mt-2 ${
                monthlyAdjustmentRounded === 0
                  ? darkMode
                    ? 'text-gray-500'
                    : 'text-gray-500'
                  : monthlyAdjustmentRounded > 0
                    ? darkMode
                      ? 'text-amber-200/85'
                      : 'text-amber-900/75'
                    : darkMode
                      ? 'text-teal-200/85'
                      : 'text-teal-900/75'
              }`}
            >
              Adds to enrolled selection (catalog)
            </div>
          </div>
          <div
            className={`rounded-xl border-2 p-5 text-center shadow-sm ${
              darkMode
                ? 'border-emerald-600/80 bg-gradient-to-br from-emerald-900/45 to-emerald-950/35'
                : 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/80'
            }`}
          >
            <div className={`text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
              Projected monthly total
            </div>
            <div className={`text-3xl font-bold tabular-nums ${darkMode ? 'text-white' : 'text-emerald-900'}`}>
              ${projectedTotalRounded.toLocaleString()}
            </div>
            <div className={`text-xs mt-2 ${darkMode ? 'text-emerald-200/90' : 'text-emerald-900/75'}`}>
              Catalog total after your edits
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            disabled={!hasChanges || submitting}
            onClick={handleSubmit}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white ${
              !hasChanges || submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting…' : 'Submit plan change request'}
          </button>
        </div>
      </div>
        </>
      )}

      {popularDetailItem && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setPopularDetailItem(null)}
          role="presentation"
        >
          <div
            className={`rounded-lg shadow-lg w-full max-w-md overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="popular-item-detail-title"
          >
            <div className="bg-red-600 p-4 relative">
              <h3 id="popular-item-detail-title" className="text-lg font-semibold text-white text-center">
                Item Details
              </h3>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="text-4xl" aria-hidden>
                  {popularDetailItem.icon || '📦'}
                </div>
                <div>
                  <h4 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {popularDetailItem.name}
                  </h4>
                  {popularDetailItem.department ? (
                    <div
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                        darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {popularDetailItem.department}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mb-6">
                <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description
                </h5>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                  {popularDetailItem.description || 'No description available.'}
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setPopularDetailItem(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
